import { BudgetSummary, ContributionCalculationType, Person, Transaction } from '../types/budget';
import i18n from '../i18n';
import { normalizeResponsible } from './normalization';
export const calculateBudgetSummary = (
  transactions: Transaction[],
  contributionCalculationType: ContributionCalculationType
): BudgetSummary => {
  const initialPersonSummary = {
    income: 0,
    expenses: 0,
    balance: 0,
    contribution: 0,
    availableBalance: 0,
  };

  // Initialize with default persons
  const personContributions: Record<Person, typeof initialPersonSummary> = {
    common: { ...initialPersonSummary },
  };

  // First, ensure all responsible persons from transactions are in the personContributions object
  transactions.forEach((transaction) => {
    const person = transaction.responsible;
    if (!personContributions[person]) {
      personContributions[person] = { ...initialPersonSummary };
    }
  });

  // Calculate individual incomes and expenses
  transactions.forEach((transaction) => {
    const amount = transaction.amount;
    const person = normalizeResponsible(transaction.responsible) as Person;
    const summary = personContributions[person];

    if (amount > 0) {
      // Ajoute les montants positifs aux revenus (toujours en valeur absolue)
      summary.income += Math.abs(amount);
    } else {
      // Ajoute les montants négatifs aux dépenses (toujours en valeur absolue)
      summary.expenses += Math.abs(amount);
    }
  });

  // Calculate individual balances
  Object.keys(personContributions).forEach((person) => {
    const summary = personContributions[person as Person];
    summary.balance = summary.income - summary.expenses; // La balance est la différence entre revenus et dépenses
  });

  // Ensure Common exists for calculations
  if (!personContributions.common) {
    personContributions.common = { ...initialPersonSummary };
  }

  // Calculate total income and expenses (excluding common budget)
  const totalIncome = Object.entries(personContributions)
    .filter(([person]) => person !== 'common')
    .reduce((sum, [_, data]) => sum + data.income, 0);

  const totalExpenses = Object.entries(personContributions)
    .filter(([person]) => person !== 'common')
    .reduce((sum, [_, data]) => sum + data.expenses, 0);

  const balance = totalIncome - totalExpenses;

  // Calculate contributions to common budget
  const commonExpenses = personContributions['common'].expenses;

  // Calculate contributions based on the selected method
  Object.keys(personContributions).forEach((person) => {
    if (person === 'common') return;

    const summary = personContributions[person as Person];
    let contribution = 0;

    switch (contributionCalculationType) {
      case ContributionCalculationType.INCOME_RATIO: {
        // Current approach - ratio based on total income
        const contributionRatio = totalIncome > 0 ? summary.income / totalIncome : 0.5;
        contribution = commonExpenses * contributionRatio;
        break;
      }

      case ContributionCalculationType.EQUAL_SPLIT: {
        // Each responsible pays an equal part
        const responsibleCount = Object.keys(personContributions).filter(
          (p) => p !== 'common'
        ).length;
        contribution = commonExpenses / responsibleCount;
        break;
      }

      case ContributionCalculationType.AFTER_PERSONAL: {
        // Ratio applied after removing personal expenses
        const personalBalance = summary.balance;
        const totalPersonalBalance = Object.entries(personContributions)
          .filter(([p]) => p !== 'common')
          .reduce((sum, [_, data]) => sum + data.balance, 0);

        const balanceRatio =
          totalPersonalBalance > 0 ? personalBalance / totalPersonalBalance : 0.5;
        contribution = commonExpenses * balanceRatio;
        break;
      }

      case ContributionCalculationType.FULL_BALANCE: {
        // Complete balance after personal expenses goes to common
        if (summary.balance > 0) {
          contribution = summary.balance;
        }
        break;
      }
    }

    summary.contribution = contribution;
    summary.availableBalance = summary.balance - contribution;
  });

  return {
    totalIncome,
    totalExpenses,
    balance,
    personContributions,
  };
};

export const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
  const locale = i18n.language || 'en';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Convert an entity id into a display label by:
 * 1) looking up the matching name in the provided collection
 * 2) falling back to a humanized version of the id (hyphens to spaces, Title Case)
 */
export const getDisplayLabel = (
  id: string,
  collection: Array<{ id: string; name: string }>
): string => {
  const found = collection.find((item) => item.id === id)?.name;
  if (found && found.trim().length > 0) return found;
  return titleCase(id.replace(/-/g, ' '));
};

const titleCase = (input: string): string => {
  return input
    .split(/\s+/)
    .map((word) => (word ? word[0].toLocaleUpperCase() + word.slice(1) : word))
    .join(' ');
};
